import { useLocation, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/hooks/useAuth';
import { useLogout } from '@/api/hooks';
import { useAlerts } from '@/api/hooks';
import { useUIStore } from '@/stores/ui';
import { useState } from 'react';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  map: 'Map',
  fields: 'Fields',
  alerts: 'Alerts',
  reports: 'Reports',
  settings: 'Settings',
};

function useBreadcrumbs() {
  const location = useLocation();
  const segments = location.pathname.replace(/^\/app\/?/, '').split('/').filter(Boolean);
  return segments.map((seg, i) => ({
    label: ROUTE_LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    to: '/app/' + segments.slice(0, i + 1).join('/'),
    isLast: i === segments.length - 1,
  }));
}

export function TopBar() {
  const breadcrumbs = useBreadcrumbs();
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const { data: alertsData } = useAlerts({ status: 'active' });
  const activeAlertCount = alertsData?.meta?.totalCount ?? 0;
  const { toggleNotificationsPanel } = useUIStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const initials = user?.fullName
    ? user.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 gap-4 shrink-0">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm min-w-0">
        <Link
          to="/app/dashboard"
          className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          aria-label="Home"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </Link>
        {breadcrumbs.map((crumb) => (
          <span key={crumb.to} className="flex items-center gap-1.5 min-w-0">
            <span className="text-gray-300">/</span>
            {crumb.isLast ? (
              <span className="font-semibold text-gray-800 truncate">{crumb.label}</span>
            ) : (
              <Link
                to={crumb.to}
                className="text-gray-500 hover:text-gray-700 truncate transition-colors"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
      </nav>

      {/* Right actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Notifications bell */}
        <button
          onClick={toggleNotificationsPanel}
          className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label={`Notifications${activeAlertCount > 0 ? ` (${activeAlertCount} active)` : ''}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
          {activeAlertCount > 0 && (
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className={clsx(
              'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
              'hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-earth-500',
            )}
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
            aria-label="User menu"
          >
            <div className="h-7 w-7 rounded-full bg-earth-600 flex items-center justify-center text-white text-xs font-semibold">
              {initials}
            </div>
            <div className="hidden md:block text-left leading-tight">
              <div className="font-medium text-gray-800 text-xs">{user?.fullName ?? 'User'}</div>
              <div className="text-gray-400 text-xs truncate max-w-[120px]">{user?.email ?? ''}</div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
          </button>

          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                aria-hidden="true"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl border border-gray-200 bg-white shadow-lg py-1">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-900 truncate">{user?.fullName}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <Link
                  to="/app/settings"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/></svg>
                  Profile & Settings
                </Link>
                <button
                  onClick={() => { setUserMenuOpen(false); logout(); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
